import { useMemo } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import {
  GALLERY_BREAKPOINT_TIERS,
  GALLERY_SPLIT_BREAKPOINT_TIERS,
} from 'components/marketplace/helpers/presizedPhotos';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { Map } from 'components/marketplace/property/Map';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';

import styles from './GalleryPhotos.module.css';

interface GalleryPhotoProps {
  photo: GalleryPhoto;
  idx: number;
  totalPhotos: number;
  address: string;
  toggleLightbox: () => void;
  setSelectedPhotoIndex: (idx: number) => void;
}

const GalleryPhotoTile = ({
  photo,
  idx,
  totalPhotos,
  address,
  toggleLightbox,
  setSelectedPhotoIndex,
}: GalleryPhotoProps) => {
  const { trackProperty } = useTrackProperty();
  const handleClick = () => {
    trackProperty('property-images', 'marketplace-pdp-lightbox');
    toggleLightbox();
    setSelectedPhotoIndex(idx);
  };

  const floorPlanLabel = photo.isFloorPlan ? photo.label?.trim() || 'Floor plan' : null;
  const alt = floorPlanLabel
    ? `${floorPlanLabel}${address ? ` of ${address}` : ''}`
    : `Photo ${idx + 1} of ${totalPhotos}${address ? ` of ${address}` : ''}`;
  const isSplitTile = idx === 1;

  return (
    <Box
      analyticsName={
        photo.isFloorPlan
          ? 'cosmos-marketplace-property-desktop-gallery-floor-plan'
          : 'cosmos-marketplace-property-desktop-gallery-photo'
      }
      aria-label={alt}
      key={`${idx}-photo`}
      onClick={handleClick}
      className={cn(
        'relative w-full shrink-0 flex-col overflow-hidden bg-od-bg-secondary p-0',
        isSplitTile ? 'h-full' : 'aspect-[3/2]',
      )}
    >
      {photo.isFloorPlan ? (
        // Floor plans do not share a consistent aspect ratio; preserve their full geometry.
        <Box className="h-full w-full">
          <GalleryListingPhoto
            photo={photo}
            sizes={isSplitTile ? GALLERY_SPLIT_BREAKPOINT_TIERS : GALLERY_BREAKPOINT_TIERS}
            lazy
            preload={idx === 0}
            fetchPriority={idx === 0 ? 'high' : undefined}
            alt={alt}
            objectFit="contain"
            className="h-full w-full"
          />
        </Box>
      ) : (
        <GalleryListingPhoto
          photo={photo}
          sizes={isSplitTile ? GALLERY_SPLIT_BREAKPOINT_TIERS : GALLERY_BREAKPOINT_TIERS}
          lazy
          preload={idx === 0}
          fetchPriority={idx === 0 ? 'high' : undefined}
          alt={alt}
          objectFit="cover"
          fit={isSplitTile ? 'cover' : undefined}
        />
      )}
      {floorPlanLabel && (
        <Box className="absolute bottom-3 left-3 rounded-lg bg-od-bg-inverse-primary px-3 py-1">
          <Text as="span" className="text-label-sm text-od-content-inverse-primary">
            {floorPlanLabel}
          </Text>
        </Box>
      )}
    </Box>
  );
};

interface GalleryPhotosProps {
  property: PropertyDetailsType;
  toggleLightbox: () => void;
  handleSetSelectedPhotoIndex: (idx: number) => void;
}
export const GalleryPhotos = ({
  property,
  toggleLightbox,
  handleSetSelectedPhotoIndex,
}: GalleryPhotosProps) => {
  const galleryPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const totalPhotos = galleryPhotos.length;
  const address = [property?.street, property?.city, property?.state].filter(Boolean).join(', ');

  const Gallery = useMemo(() => {
    const photos = galleryPhotos.map((photo, idx) => {
      const galleryPhoto = (
        <GalleryPhotoTile
          key={idx}
          idx={idx}
          totalPhotos={totalPhotos}
          address={address}
          photo={photo}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
        />
      );

      if (idx == 1) {
        return (
          <Row key="split-photo-map-view" className="w-full justify-between">
            <Box className="aspect-[16/9] h-full w-[49.75%] overflow-hidden">{galleryPhoto}</Box>
            <Box className="aspect-[16/9] w-[49.75%] bg-od-bg-secondary">
              <Map
                key="property-location"
                latitude={property.latitude}
                longitude={property.longitude}
              />
            </Box>
          </Row>
        );
      }

      return galleryPhoto;
    });

    return photos;
  }, [
    galleryPhotos,
    totalPhotos,
    address,
    property.latitude,
    property.longitude,
    toggleLightbox,
    handleSetSelectedPhotoIndex,
  ]);
  return (
    <Col
      className={cn(
        styles.hideScrollbar,
        'h-full w-full items-center gap-3 max-md:hidden md:basis-1/2 md:overflow-scroll xl:flex-1',
      )}
    >
      {Gallery}
    </Col>
  );
};
